/**
 * TopHeader — sticky app bar. Orchestrates three slots:
 *
 *   - Left:   tablet hamburger + (owner stats | world clocks).
 *   - Middle: GlobalSearch (desktop input + mobile overlay trigger).
 *   - Right:  HeaderActions cluster (theme, settings, schedule, avatar).
 *
 * Owner trip-stats fetch + schedule-flight modal mount stay here so they
 * have a single source of truth and don't get duplicated in subcomponents.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { buildDefaultClocks } from "@/lib/timezones";
import NewTripModal from "@/components/NewTripModal";
import CoachMark from "@/components/CoachMark";
import WorldClocks from "./topheader/WorldClocks";
import OwnerStats from "./topheader/OwnerStats";
import GlobalSearch from "./topheader/GlobalSearch";
import HeaderActions from "./topheader/HeaderActions";

export default function TopHeader({ mobileMenuButton }) {
  const { user } = useAuth();
  const { t } = useTranslation();

  const isOwner = user?.role === "owner";
  const isOpsManager = user?.role === "ops_manager";
  const [ownerStats, setOwnerStats] = useState(null);

  // "Programar vuelo" — modal global, lazy-load aircraftList al abrir.
  // Vive en TopHeader (en lugar de un FAB flotante por página) para estar
  // disponible en toda la app sin tapar contenido.
  const [scheduleOpen, setScheduleOpen] = useState(false);
  // Aircraft list for the global "Programar vuelo" modal. Uses the SHARED
  // ["/aircraft"] React Query cache — NOT a one-shot local snapshot — so a newly
  // added aircraft (invalidateAircraft() marks that key stale app-wide) shows up
  // here immediately, instead of staying frozen until a full PWA restart
  // (bug 2026-07-18: the old `if (!scheduleAircraft)` guard fetched once per
  // session and never refreshed). Fetches lazily when the modal first opens; if
  // another page (calendar/fleet/dashboard) already loaded it, it's instant.
  const { data: scheduleAircraft = [] } = useQuery({
    queryKey: ["/aircraft"],
    queryFn: () => api.get("/aircraft").then((r) => r.data),
    enabled: scheduleOpen,
  });
  const openSchedule = useCallback(() => setScheduleOpen(true), []);

  useEffect(() => {
    if (!isOwner) return;
    api
      .get("/owner/trip-stats")
      .then((res) => {
        const { completed, hours_flown, yearly_trips, yearly_hours } = res.data;
        setOwnerStats({
          completed,
          hoursFlown: hours_flown,
          yearlyTrips: yearly_trips,
          yearlyHours: yearly_hours,
        });
      })
      .catch(console.error);
  }, [isOwner]);

  // Default order for new users: UTC → base timezone → major city in base's country.
  // Memo'd: both branches build a fresh array each call, which would break
  // WorldClocks' React.memo barrier (its only prop is this array ref).
  const userClocks = useMemo(
    () => user?.settings?.clocks || buildDefaultClocks(user?.settings?.ops_timezone),
    [user?.settings?.clocks, user?.settings?.ops_timezone]
  );
  // Sliced variant for the mobile breakpoint — also memo'd so the array
  // identity is stable across unrelated parent re-renders.
  const mobileClocks = useMemo(() => userClocks.slice(0, 1), [userClocks]);

  return (
    <>
      {/* Fondo SÓLIDO, sin `backdrop-blur`. No es cosmético:
          `backdrop-filter` obliga al compositor a poner en una capa aparte de
          la GPU todo lo que puede quedar detrás de esta barra — y como es
          `sticky` y vive en TODAS las pantallas, esa capa es el área de
          contenido entera.

          Cuando la ventana de la app INSTALADA pierde el foco (te vas a otra
          ventana y regresas), el sistema suspende su dibujado; al volver, la
          capa no se reconstruye y Chromium muestra la última versión que tenía
          en memoria: desenfocada y congelada. Encaja con los cuatro síntomas
          que se reportaron — tamaño correcto, se puede hacer clic encima, en
          cualquier pantalla, y sólo se arregla cerrando y reabriendo la app.
          En una pestaña normal casi no pasa porque el navegador la mantiene
          más viva; en una ventana sin pestañas Windows es más agresivo.

          Ya hay precedente escrito en este repo por la MISMA razón: el
          `blur(4px)` del overlay de modales se quitó porque re-rasterizar por
          cuadro costaba caro en gráficos integrados (ver index.css). Éste era
          el que quedaba, y con `xl` = 24 px, seis veces más fuerte.

          El fondo pasa de 80% a sólido: sobre una barra que ya era casi opaca
          el desenfoque casi no se notaba, así que visualmente no cambia nada. */}
      <header
        data-testid="top-header"
        className="bg-[#0f172a] border-b border-slate-800 flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30 py-2 sm:py-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.5rem)" }}
      >
        {/* `min-w-0` en el grupo IZQUIERDO: sin él un flex hijo no baja de su
            contenido (`min-width: auto`), así que el excedente no se recorta —
            empuja al grupo derecho fuera del viewport y toda la app gana scroll
            horizontal. Los relojes son lo que puede ceder; la búsqueda y las
            acciones no. Es la red de seguridad estructural: si mañana crece
            cualquiera de los dos lados, el desborde ya no puede volver. */}
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          {/* Tablet hamburger — hidden on mobile xs (bottom nav handles it) */}
          {mobileMenuButton}

          {/* Owner trip stats replace clocks for owner role */}
          {isOwner ? (
            <OwnerStats stats={ownerStats} />
          ) : (
            <>
              {/* World Clocks — 1 clock on mobile, up to 3 on desktop */}
              <div className="sm:hidden">
                <WorldClocks userClocks={mobileClocks} />
              </div>
              <div className="hidden sm:flex">
                <WorldClocks userClocks={userClocks} />
              </div>
            </>
          )}
        </div>

        {/* El grupo derecho NO cede: la búsqueda y el avatar son destinos de
            clic, no adorno. Decirlo explícito deja escrito quién encoge. */}
        <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
          <GlobalSearch showMobileButton={!isOwner} />
          <HeaderActions user={user} isOpsManager={isOpsManager} onOpenSchedule={openSchedule} />
        </div>
      </header>

      {/* Coach mark del botón "+" — apunta al CTA en el header. Sólo mobile,
          sólo ops_manager, una vez por usuario (id versionado tras la
          reubicación del botón al header). */}
      {isOpsManager && (
        <CoachMark
          id="header_schedule_v2"
          title={t("dashboard.coachFabTitle")}
          body={t("dashboard.coachFabBody")}
          position="top-right"
        />
      )}

      {scheduleOpen && (
        <NewTripModal
          aircraft={scheduleAircraft}
          userRole={user?.role}
          onClose={() => setScheduleOpen(false)}
          onSuccess={() => setScheduleOpen(false)}
        />
      )}
    </>
  );
}
